import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Chip, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import { Pager, num, pageInfo, rowsOf, str, useDebounced } from '../ipintel/shared';
import { ExcelButton, KNOWN_INDUSTRIES } from '../techpages/shared';
import { AdminLink } from '../adminNav';
import {
  CONFIDENCES, ConfidencePill, EXPORT_PATH, HORIZONS, type HzCtx, HorizonPill, LEVERS, LeverChips, type Preset,
  S, StatusPill, TECH_EMPTY, type TechFilters, applyPreset, clip, horizonLabel, leversOf, promptLabel, publicUrl,
  uniOptions,
} from './shared';

/**
 * Technologies — GET horizon/technologies: every technology in the corpus,
 * whether it has Horizon data (status has / pending) and its current data
 * (horizon, confidence, industry, value levers), with every filter the
 * handler takes and server-side sorting and paging. A row opens the
 * technology's drawer; the public page and its Tech URL pages entry are one
 * click away. The Excel export takes the same filters.
 */

/** Table column → the handler's sort key. Columns not listed do not sort. */
const SORT_KEYS: Record<string, string> = {
  title: 'title', university_label: 'university', status: 'status', horizon: 'horizon', confidence: 'confidence',
  horizon_industry: 'horizon_industry', industry: 'industry', current_at: 'updated', results_count: 'results',
};
const COLUMNS = ['title', 'university_label', 'status', 'horizon', 'confidence', 'horizon_industry', 'levers',
  'industry', 'results_count', 'prompts', 'current_at'];
const LABELS: Record<string, string> = {
  title: 'Technology', university_label: 'University', status: 'Status', horizon: 'Horizon', confidence: 'Confidence',
  horizon_industry: 'Horizon industry', levers: 'Value levers', industry: 'Industry (corpus)',
  results_count: 'Results', prompts: 'Prompts', current_at: 'Current result',
};
const STATUS_LABELS: Record<string, string> = { all: 'Has data or pending', has: 'Has Horizon data', pending: 'Pending (no data)' };

const HzTechnologies: React.FC<{ ctx: HzCtx; preset: Preset<TechFilters> }> = ({ ctx, preset }) => {
  const last = useRef(preset?.n || 0);
  const [f, setF] = useState<TechFilters>(() => ({ ...TECH_EMPTY, ...(preset?.f || {}) }));
  useEffect(() => { applyPreset(preset, TECH_EMPTY, setF, last); }, [preset]);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>({ key: 'title', dir: 'asc' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const set = (k: keyof TechFilters) => (v: string) => setF(s => ({ ...s, [k]: v }));
  const q = useDebounced(f.q);
  const industry = useDebounced(f.industry);
  const tag = useDebounced(f.tag);

  const filterParams = useMemo(() => {
    const p: Record<string, string | number> = {
      q, university: f.university, industry, horizon: f.horizon, confidence: f.confidence, lever: f.lever, tag,
      status: f.status || 'all', prompt: f.prompt,
    };
    if (sort?.key && SORT_KEYS[sort.key]) { p.sort = SORT_KEYS[sort.key]; p.dir = sort.dir; }
    return p;
  }, [q, industry, tag, f.university, f.horizon, f.confidence, f.lever, f.status, f.prompt, sort]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'technologies', { ...filterParams, page, per_page: perPage },
    [filterKey, page, perPage, tick, ctx.dataTick]);
  const d = res.data || {};
  const rows = rowsOf(d, 'technologies');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rows, per);

  const uni = uniOptions(ctx.universities);
  const uniLabel = (code: string) => uni.labels[code] || code;
  // The handler's facets (corpus + Horizon industries) when it sends them, else the known lists.
  const facetInd = useMemo(() => [...rowsOf(d.facets?.industries), ...rowsOf(d.facets?.horizon_industries)]
    .map((x: any) => str(x?.name ?? x)).filter(Boolean), [d.facets]);
  const industries = useMemo(() => Array.from(new Set([...facetInd, ...ctx.industries, ...KNOWN_INDUSTRIES])).sort(),
    [facetInd, ctx.industries]);

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.status && f.status !== 'all') chips.push({ label: 'Status', value: STATUS_LABELS[f.status] || f.status, clear: () => set('status')('all') });
  if (f.university) chips.push({ label: 'University', value: uniLabel(f.university), clear: () => set('university')('') });
  if (f.industry) chips.push({ label: 'Industry', value: f.industry, clear: () => set('industry')('') });
  if (f.horizon) chips.push({ label: 'Horizon', value: horizonLabel(f.horizon), clear: () => set('horizon')('') });
  if (f.confidence) chips.push({ label: 'Confidence', value: f.confidence, clear: () => set('confidence')('') });
  if (f.lever) chips.push({ label: 'Lever', value: f.lever, clear: () => set('lever')('') });
  if (f.tag) chips.push({ label: 'Tag', value: f.tag, clear: () => set('tag')('') });
  if (f.prompt) chips.push({ label: 'Has a result from', value: promptLabel(ctx.promptById(f.prompt), f.prompt), clear: () => set('prompt')('') });

  const promptOpts = ['', ...ctx.prompts.map(p => p.id)];
  const promptLabels: Record<string, string> = {
    '': 'Any prompt', ...Object.fromEntries(ctx.prompts.map(p => [p.id, clip(`${p.is_final ? '★ ' : ''}${promptLabel(p)}`, 40)])),
  };

  return (
    <Section title="Technologies"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">{total.toLocaleString()} technolog{total === 1 ? 'y' : 'ies'}</span>
          {chips.length > 0 && <Btn small onClick={() => setF(TECH_EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
          <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
          <ExcelButton path={EXPORT_PATH} params={{ kind: 'technologies', ...filterParams }} onError={setDlErr}
            fallback="horizon-technologies" what="These technologies" />
        </div>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Every technology and its current Horizon data — what its public page shows. Pending = no usable result under any
        prompt in the source priority yet. Click a title for the current data and every result it ever got.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={f.q} onChange={set('q')} wide placeholder="Search title or tech id…" />
        <Select value={f.status || 'all'} onChange={set('status')} options={['all', 'has', 'pending']} labels={STATUS_LABELS} />
        <Select value={f.university} onChange={set('university')} options={uni.options} labels={uni.labels} />
        <input list="hz-tech-industries" value={f.industry} onChange={e => set('industry')(e.target.value)}
          placeholder="Industry…" title="Industry (pick one or type)"
          className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-56 max-w-full" />
        <datalist id="hz-tech-industries">{industries.map(i => <option key={i} value={i} />)}</datalist>
        <Select value={f.horizon} onChange={set('horizon')} options={['', ...HORIZONS]}
          labels={{ '': 'Any horizon', ...Object.fromEntries(HORIZONS.map(h => [h, horizonLabel(h)])) }} />
        <Select value={f.confidence} onChange={set('confidence')} options={['', ...CONFIDENCES]} labels={{ '': 'Any confidence' }} />
        <Select value={f.lever} onChange={set('lever')} options={['', ...LEVERS]} labels={{ '': 'Any value lever' }} />
        <TextInput value={f.tag} onChange={set('tag')} placeholder="Technical tag…" />
        <Select value={f.prompt} onChange={set('prompt')} options={promptOpts} labels={promptLabels} />
      </div>
      {chips.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
        </div>
      )}
      {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Reading technologies…" /> : (
        <DataTable rows={rows} columns={COLUMNS} labels={LABELS} maxHeight="44rem"
          unsortable={COLUMNS.filter(c => !SORT_KEYS[c])}
          empty={chips.length ? 'No technologies match these filters.' : 'No technologies.'}
          serverSort={sort} onSortChange={(key, dir) => setSort(key && dir ? { key, dir } : null)}
          render={{
            title: r => (
              <span className="flex flex-col leading-tight max-w-[22rem]">
                <button type="button" onClick={() => ctx.openTech(str(r.id))} title="Open this technology's Horizon data"
                  className="text-left font-medium text-grayscale-1300 underline underline-offset-2 truncate">
                  {str(r.title) || str(r.id)}
                </button>
                <span className="text-[10px] font-mono text-grayscale-1100 truncate">{str(r.id)}</span>
              </span>
            ),
            university_label: r => <span title={str(r.university_code)}>{str(r.university_label) || str(r.university_code) || '—'}</span>,
            status: r => <StatusPill status={r.status} noSummary={r.status === 'pending' && r.has_summary === false} />,
            horizon: r => <HorizonPill h={r.horizon} />,
            confidence: r => <ConfidencePill c={r.confidence} />,
            horizon_industry: r => <span className="block max-w-[14rem] truncate" title={str(r.horizon_industry)}>{str(r.horizon_industry) || '—'}</span>,
            levers: r => <LeverChips levers={leversOf(r.levers)} max={2} compact total={num(r.levers_count)} />,
            industry: r => <span className="block max-w-[14rem] truncate" title={str(r.industry)}>{str(r.industry) || '—'}</span>,
            results_count: r => <span className="tabular-nums">{num(r.results_count).toLocaleString()}</span>,
            prompts: r => {
              const ids: string[] = Array.isArray(r.prompts) ? r.prompts.map(str) : [];
              if (!ids.length) return <span className="text-grayscale-1100">—</span>;
              const final = ids.some(id => ctx.promptById(id)?.is_final);
              return (
                <span className="text-xs" title={ids.map(id => promptLabel(ctx.promptById(id), id)).join('\n')}>
                  {ids.length}{final ? ' · incl. final' : ''}
                </span>
              );
            },
            current_at: r => <span className="text-xs">{r.current_at ? fmtDate(r.current_at) : '—'}</span>,
          }}
          actions={r => (
            <span className="inline-flex items-center gap-2">
              <Btn small onClick={() => ctx.openTech(str(r.id))}>Details</Btn>
              {r.tech_page_path ? (
                <a href={publicUrl(r.tech_page_path)} target="_blank" rel="noopener noreferrer"
                  title={`Open ${publicUrl(r.tech_page_path)}`} className="text-primary-700 underline underline-offset-2 whitespace-nowrap">
                  Public ↗
                </a>
              ) : <span className="text-xs text-grayscale-1100" title="No public page URL yet">no page</span>}
              <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: str(r.id) }} title="Open this page in the Tech URL pages admin">
                Tech URL page
              </AdminLink>
            </span>
          )} />
      )}
      {pages > 1 && (
        <Pager page={page} pages={pages} total={total} shown={rows.length} per={per}
          onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
      )}
    </Section>
  );
};

export default HzTechnologies;
